import React, { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Lock, Globe2, Activity, AlertTriangle, Unlock, CheckCircle2, UserPlus, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { exportToPDF } from "@/lib/pdf";

export default function SecurityAdmin() {
  const [sum, setSum] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [events, setEvents] = useState([]);
  const [locked, setLocked] = useState([]);
  const [recovery, setRecovery] = useState([]);
  const [pwAges, setPwAges] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "physician", specialty: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const load = () => {
    api.get("/security/summary").then(r=>setSum(r.data));
    api.get("/security/login-attempts").then(r=>setAttempts(r.data));
    api.get("/security/events").then(r=>setEvents(r.data));
    api.get("/security/locked-accounts").then(r=>setLocked(r.data));
    api.get("/security/recovery-requests").then(r=>setRecovery(r.data));
    api.get("/security/password-ages").then(r=>setPwAges(r.data));
    api.get("/invites").then(r=>setInvites(r.data));
  };
  useEffect(load, []);

  const unlock = async (uid) => {
    await api.post(`/security/unlock/${uid}`);
    toast.success("Account unlocked");
    load();
  };

  const approveRecovery = async (id) => {
    await api.post(`/security/recovery/${id}/approve`);
    toast.success("Recovery approved");
    load();
  };
  const rejectRecovery = async (id) => {
    await api.post(`/security/recovery/${id}/reject`);
    toast.success("Recovery rejected");
    load();
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/invites", inviteForm);
      setInviteResult(data);
      toast.success("Invite created. Copy the credentials below.");
      setInviteForm({ email: "", name: "", role: "physician", specialty: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create invite");
    } finally { setInviting(false); }
  };

  const resetPassword = async (email, clearTwofa) => {
    const customPw = window.prompt(
      `Reset password for ${email}\n\nLeave blank for an auto-generated temporary password, or enter a custom one (min 8 chars).${clearTwofa ? "\n\n⚠ 2FA will also be cleared — user must re-enrol on next login." : ""}`,
      ""
    );
    if (customPw === null) return;  // cancelled
    setResettingId(email);
    setResetResult(null);
    try {
      const { data } = await api.post("/admin/users/reset-password", {
        email,
        new_password: customPw || null,
        require_2fa_reset: clearTwofa,
      });
      setResetResult(data);
      toast.success("Password reset. Copy the new credentials below.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setResettingId(null); }
  };

  const copyCreds = () => {
    if (!inviteResult) return;
    // Sign-in URL uses the ADMIN's current origin so the invite always
    // matches the domain the admin is signed into.  Falls back to the
    // canonical production domain (zidamd.com) if window is unavailable.
    const signInUrl = typeof window !== "undefined" ? window.location.origin : "https://app.zidamd.com";
    const text = `ZidaMD account credentials\n\nEmail: ${inviteResult.email}\nTemporary password: ${inviteResult.temp_password}\n\nSign in at ${signInUrl} — you'll be prompted to set up 2FA, accept the clinical disclaimer, and change your password on first login.`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied to clipboard");
  };

  const exportLog = () => {
    const txt = events.slice(0, 100).map(e =>
      `${e.timestamp} • ${e.type} • IP ${e.ip || "—"} • ${JSON.stringify(e.geo || {})}`
    ).join("\n");
    exportToPDF("Security Event Log (last 100)", txt);
  };

  const attemptCols = [
    { key: "timestamp", label: "Time", render: (r)=>new Date(r.timestamp).toLocaleString() },
    { key: "email", label: "Email" },
    { key: "ip", label: "IP" },
    { key: "country", label: "Country" },
    { key: "city", label: "City" },
    { key: "success", label: "Result", render: (r)=>r.success?<span className="status-pill status-approved">success</span>:<span className="status-pill status-denied">failed</span> },
    { key: "reason", label: "Reason" },
  ];

  const eventCols = [
    { key: "timestamp", label: "Time", render: (r)=>new Date(r.timestamp).toLocaleString() },
    { key: "type", label: "Event", render: (r)=>(
      <span className="status-pill status-denied font-mono text-[10px]">{r.type}</span>
    )},
    { key: "ip", label: "IP" },
    { key: "path", label: "Path / details", render: (r)=>r.path || r.attack || (r.geo && r.geo.country) || "—" },
  ];

  const lockedCols = [
    { key: "email", label: "Email" },
    { key: "name", label: "Name" },
    { key: "role", label: "Role" },
    { key: "failed_logins", label: "Failed attempts" },
    { key: "locked_until", label: "Locked until", render: (r)=>r.locked_until?new Date(r.locked_until).toLocaleString():"—" },
    { key: "actions", label: "", sortable: false, render: (r)=>(
      <button onClick={()=>unlock(r.id)} className="text-xs px-2 py-1 rounded bg-brand-teal text-white inline-flex items-center gap-1" data-testid={`unlock-${r.id}`}>
        <Unlock className="w-3.5 h-3.5" /> Unlock
      </button>
    )},
  ];

  const recovCols = [
    { key: "submitted_at", label: "Submitted", render: (r)=>new Date(r.submitted_at).toLocaleString() },
    { key: "email", label: "Email" },
    { key: "ip", label: "IP" },
    { key: "status", label: "Status", render: (r)=><span className={`status-pill status-${r.status}`}>{r.status}</span> },
    { key: "actions", label: "", sortable: false, render: (r)=> r.status === "pending" ? (
      <div className="flex gap-1.5">
        <button onClick={()=>approveRecovery(r.id)} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">Approve</button>
        <button onClick={()=>rejectRecovery(r.id)} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Reject</button>
      </div>
    ) : "—" },
  ];

  const pwCols = [
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
    { key: "password_age_days", label: "PW age (days)" },
    { key: "status", label: "Status", render: (r)=><span className={`status-pill ${r.status==="overdue"?"status-denied":r.status==="soon"?"status-pending":"status-approved"}`}>{r.status}</span> },
    { key: "twofa_enabled", label: "2FA", render: (r)=>r.twofa_enabled?<CheckCircle2 className="w-4 h-4 text-emerald-600" />:<AlertTriangle className="w-4 h-4 text-red-500" /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Security Command Center" title="Security Admin"
        subtitle="Real-time login map, WAF block log, locked accounts, 2FA compliance, password age, recovery queue. Admin role only."
        actions={<button onClick={exportLog} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg" data-testid="export-log-btn">Export event log PDF</button>}
        testid="sec-page" />

      {sum && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <KpiCard icon={ShieldCheck} label="2FA compliance" value={`${sum.twofa_compliance_pct}%`} hint={`${sum.twofa_enabled}/${sum.users_total} users`} testid="kpi-2fa" />
          <KpiCard icon={Lock} label="Locked" value={sum.locked_accounts} accent testid="kpi-locked" />
          <KpiCard icon={Activity} label="Login attempts 24h" value={sum.login_attempts_24h} testid="kpi-att24" />
          <KpiCard icon={AlertTriangle} label="Failed 24h" value={sum.failed_attempts_24h} testid="kpi-fail24" />
          <KpiCard icon={ShieldAlert} label="Blocked events" value={sum.blocked_events_24h} testid="kpi-blocked" />
          <KpiCard icon={Globe2} label="Countries seen 7d" value={sum.geo_map.length} testid="kpi-geo" />
          <KpiCard label="Recovery queue" value={sum.pending_recovery} testid="kpi-recov" />
        </div>
      )}

      {sum?.geo_map?.length > 0 && (
        <SectionCard title="Login Attempts by Country (7d)" testid="geo-map">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {sum.geo_map.map((g,i)=>(
              <div key={i} className="p-3 bg-brand-bg rounded-md">
                <div className="text-xs text-brand-muted">{g.countryCode}</div>
                <div className="text-sm font-semibold truncate">{g.country}</div>
                <div className="text-lg font-semibold text-brand-primary">{g.count}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Invite Beta User" subtitle="Creates an account with a one-time temporary password. They'll be forced through 2FA + disclaimer + password change on first login." testid="invite-section">
          <form onSubmit={sendInvite} className="space-y-2">
            <input type="email" required placeholder="Email" value={inviteForm.email} onChange={(e)=>setInviteForm({...inviteForm, email:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="invite-email" />
            <input required placeholder="Full name (e.g. Dr. Jane Smith)" value={inviteForm.name} onChange={(e)=>setInviteForm({...inviteForm, name:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="invite-name" />
            <div className="grid grid-cols-2 gap-2">
              <select value={inviteForm.role} onChange={(e)=>setInviteForm({...inviteForm, role:e.target.value})}
                className="px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="invite-role">
                <option value="physician">Physician (full clinical)</option>
                <option value="np_pa">NP / PA (requires co-sign)</option>
                <option value="ma_scribe">Medical Assistant / Scribe</option>
                <option value="front_desk">Front Desk / Admin</option>
                <option value="admin">Super Admin</option>
              </select>
              <input placeholder="Specialty (optional)" value={inviteForm.specialty} onChange={(e)=>setInviteForm({...inviteForm, specialty:e.target.value})}
                className="px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            </div>
            <button type="submit" disabled={inviting}
              className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="invite-submit">
              <UserPlus className="w-4 h-4" /> {inviting ? "Creating…" : "Create invite"}
            </button>
          </form>
          {inviteResult && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm" data-testid="invite-result">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-emerald-900">Credentials created</div>
                <button onClick={copyCreds} className="text-xs px-2 py-1 rounded bg-white border border-emerald-200 inline-flex items-center gap-1" data-testid="copy-creds">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <div className="font-mono text-xs space-y-1 text-emerald-900">
                <div>Email: <span className="font-semibold">{inviteResult.email}</span></div>
                <div>Temp password: <span className="font-semibold select-all">{inviteResult.temp_password}</span></div>
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">{inviteResult.message}</div>
            </div>
          )}
        </SectionCard>

        <SectionCard title={`Invited Users (${invites.length})`} testid="invites-list">
          {invites.length === 0 ? (
            <div className="text-sm text-brand-muted py-6 text-center">No invites yet</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {invites.map(u => (
                <div key={u.id} className="p-2.5 border border-brand rounded-md flex items-center justify-between gap-3" data-testid={`invite-row-${u.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-brand-muted truncate">{u.email} • {u.role}{u.specialty?` • ${u.specialty}`:""}</div>
                  </div>
                  <div className="text-right text-[11px] shrink-0 flex flex-col items-end gap-1">
                    {u.twofa_enabled ? <span className="status-pill status-approved">2FA ✓</span> : <span className="status-pill status-pending">2FA pending</span>}
                    <div className="text-brand-muted">Invited {new Date(u.invited_at).toLocaleDateString()}</div>
                    <div className="flex gap-1">
                      <button onClick={() => resetPassword(u.email, false)} disabled={resettingId === u.email}
                        title="Reset password (keep 2FA)"
                        className="text-[10px] px-2 py-0.5 rounded border border-brand bg-white hover:bg-brand-bg inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="w-3 h-3" /> Reset PW
                      </button>
                      <button onClick={() => resetPassword(u.email, true)} disabled={resettingId === u.email}
                        title="Reset password AND clear 2FA (user must re-enrol)"
                        className="text-[10px] px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`reset-pw-2fa-${u.id}`}>
                        Reset PW + 2FA
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {resetResult && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm" data-testid="reset-result">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-emerald-900">Password reset</div>
                <button onClick={() => { navigator.clipboard.writeText(`Email: ${resetResult.email}\nPassword: ${resetResult.new_password}`); toast.success("Copied"); }}
                  className="text-xs px-2 py-1 rounded bg-white border border-emerald-200 inline-flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <div className="font-mono text-xs space-y-1 text-emerald-900">
                <div>Email: <span className="font-semibold">{resetResult.email}</span></div>
                <div>New password: <span className="font-semibold select-all">{resetResult.new_password}</span></div>
                {resetResult.twofa_cleared && <div className="text-amber-800 font-semibold">⚠ 2FA cleared — user must re-enrol</div>}
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">{resetResult.message}</div>
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Locked Accounts" testid="locked-section">
          <DataTable data={locked} columns={lockedCols} searchKeys={["email","name"]} testid="locked-table" emptyMessage="No locked accounts" />
        </SectionCard>
        <SectionCard title="Recovery Queue" testid="recov-section">
          <DataTable data={recovery} columns={recovCols} searchKeys={["email","status"]} testid="recov-table" emptyMessage="No pending requests" />
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Recent Login Attempts" testid="attempts-section">
          <DataTable data={attempts} columns={attemptCols} searchKeys={["email","ip","country","reason"]} testid="attempts-table" />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="WAF & Security Events" testid="events-section">
          <DataTable data={events} columns={eventCols} searchKeys={["type","ip","path"]} testid="events-table" />
        </SectionCard>
        <SectionCard title="Password Age & 2FA Status" testid="pw-section">
          <DataTable data={pwAges} columns={pwCols} searchKeys={["email","role","status"]} testid="pw-table" />
        </SectionCard>
      </div>
    </>
  );
}
